import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Baby } from "lucide-react";
import { toast } from "sonner";
import { getPendingInvitationToken, clearPendingInvitationToken } from "@/utils/pendingInvitation";

interface PendingItem {
  key: string;
  token: string | null; // null = email-based invitation
  babyName: string | null;
  ownerName: string | null;
}

/**
 * Viser ventende delingsinvitasjoner for innlogget bruker:
 * - lenke-invitasjoner som er lagret lokalt / på kontoen ved registrering
 * - invitasjoner sendt til brukerens e-postadresse
 */
export function PendingInvitationBanner() {
  const { user } = useAuth();
  const location = useLocation();
  const [items, setItems] = useState<PendingItem[]>([]);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    if (!user || location.pathname.startsWith("/accept-invitation")) {
      setItems([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const found: PendingItem[] = [];
      const token = getPendingInvitationToken(user.user_metadata?.pending_invite_token);
      if (token) {
        const { data } = await supabase.functions.invoke("accept-invitation", {
          body: { token, preview: true },
        });
        if (data?.found && !data.already_accepted) {
          found.push({ key: token, token, babyName: data.baby_name, ownerName: data.owner_name });
        } else {
          await clearPendingInvitationToken();
        }
      }
      if (user.email) {
        const { data } = await supabase
          .from("shared_access")
          .select("id, baby:baby_id(name)")
          .eq("invited_email", user.email.toLowerCase())
          .is("accepted_at", null)
          .neq("owner_user_id", user.id);
        if (data && data.length > 0) {
          const names = (data as any[]).map((r) => r.baby?.name).filter(Boolean).join(", ");
          found.push({ key: "email", token: null, babyName: names || null, ownerName: null });
        }
      }
      if (!cancelled) setItems(found);
    })().catch((e) => console.error("Pending invitation check failed", e));
    return () => { cancelled = true; };
  }, [user, location.pathname]);

  const accept = async (item: PendingItem) => {
    setBusy(item.key);
    try {
      const { data, error } = await supabase.functions.invoke("accept-invitation", {
        body: item.token ? { token: item.token } : {},
      });
      if (error) throw error;
      if (item.token) await clearPendingInvitationToken();
      if (data?.accepted_count > 0 || data?.already_accepted) {
        toast.success("Invitasjon akseptert!", {
          description: item.babyName ? `${item.babyName} er lagt til i din profil` : undefined,
        });
        setTimeout(() => window.location.reload(), 600);
      } else {
        toast.info("Invitasjonen er ikke lenger gyldig");
      }
      setItems((prev) => prev.filter((i) => i.key !== item.key));
    } catch {
      toast.error("Kunne ikke akseptere invitasjonen");
    } finally {
      setBusy(null);
    }
  };

  const dismiss = async (item: PendingItem) => {
    if (item.token) await clearPendingInvitationToken();
    setItems((prev) => prev.filter((i) => i.key !== item.key));
  };

  if (items.length === 0) return null;

  return (
    <div className="px-4 pt-3 space-y-2 max-w-3xl mx-auto w-full">
      {items.map((item) => (
        <Card key={item.key} className="border-primary/40 bg-card">
          <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className="rounded-full bg-primary/10 p-2 shrink-0">
                <Baby className="h-5 w-5 text-primary" />
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-foreground">Ventende delingsinvitasjon</p>
                <p className="text-sm text-muted-foreground break-words">
                  {item.ownerName ? `${item.ownerName} vil dele ` : "Du er invitert til å følge "}
                  {item.babyName ?? "et barn"} med deg.
                </p>
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button size="sm" variant="ghost" disabled={busy === item.key} onClick={() => dismiss(item)}>
                {item.token ? "Avslå" : "Senere"}
              </Button>
              <Button size="sm" disabled={busy === item.key} onClick={() => accept(item)}>
                {busy === item.key ? "Legger til..." : "Godta"}
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
